import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { convertBlobPatternsToHtml } from '../extensions/VariableBlobExtension';
import type { ClauseDetailData, ClauseVersion } from '../shared/playbook-types';
import { useAppContext } from '../context/AppContext';
import { ClauseVersionModal } from './ClauseVersionModal';
import { ConfirmationModal } from './ConfirmationModal';
import * as api from '../services/apiClient';
import { logger } from '../utils/logger';
import '../styles/PlaybookCommon.css';
import '../styles/MesContratsPage.css';

interface ClauseDetailProps {
    clause: ClauseDetailData | null;
    isLoading: boolean;
    error?: string | null;
    onRetry?: () => void;
    onEdit?: (id: number, name: string) => void;
    onDelete?: (id: number, name: string) => void;
    isAdmin?: boolean;
    onRefresh?: () => void;
}

export const ClauseDetail: React.FC<ClauseDetailProps> = ({ clause, isLoading, error, onRetry, onEdit, onDelete, isAdmin, onRefresh }) => {
    // Track expanded versions - first version expanded by default
    const [expandedVersionIds, setExpandedVersionIds] = useState<Set<number>>(new Set());
    const { setActivePage, setInitialContractId } = useAppContext();

    // Version edit modal state
    const [versionToEdit, setVersionToEdit] = useState<ClauseVersion | null>(null);
    const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

    // Version add modal state
    const [isAddVersionModalOpen, setIsAddVersionModalOpen] = useState(false);

    // Version delete confirmation state
    const [versionToDelete, setVersionToDelete] = useState<ClauseVersion | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Mounted ref for cleanup
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Handler pour naviguer vers un contrat
    const handleContractClick = (contractId: number) => {
        setInitialContractId(contractId);
        setActivePage('page4'); // page4 = Mes Contrats
    };

    // Reset expanded state when clause changes, expand first version by default
    useEffect(() => {
        if (clause && clause.versions.length > 0) {
            setExpandedVersionIds(new Set([clause.versions[0].id]));
        } else {
            setExpandedVersionIds(new Set());
        }
    }, [clause?.id]);

    const toggleVersion = (versionId: number) => {
        setExpandedVersionIds(prev => {
            const next = new Set(prev);
            if (next.has(versionId)) {
                next.delete(versionId);
            } else {
                next.add(versionId);
            }
            return next;
        });
    };

    // Helper pour s'assurer que le lien a un protocole
    const ensureProtocol = (url: string | undefined | null): string => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        return `https://${url}`;
    };

    // Handlers pour édition/suppression de version
    const handleEditVersion = useCallback((version: ClauseVersion) => {
        setVersionToEdit(version);
        setIsVersionModalOpen(true);
    }, []);

    const handleDeleteVersionRequest = useCallback((version: ClauseVersion) => {
        setVersionToDelete(version);
        setIsDeleteModalOpen(true);
    }, []);

    const handleDeleteVersionConfirm = useCallback(async () => {
        if (!versionToDelete) return;

        setIsDeleting(true);
        setDeleteError(null);
        try {
            const response = await api.deleteClauseVersion(versionToDelete.id);
            if (!isMountedRef.current) return;

            if (response.success) {
                logger.info(`Version supprimée: ${versionToDelete.id}`);
                setIsDeleteModalOpen(false);
                setVersionToDelete(null);
                onRefresh?.();
            } else {
                logger.error('Erreur suppression version:', response.error);
                setDeleteError(response.error || 'Erreur lors de la suppression');
            }
        } catch (err) {
            if (!isMountedRef.current) return;
            logger.error('Erreur suppression version:', err);
            setDeleteError('Erreur lors de la suppression');
        } finally {
            if (isMountedRef.current) {
                setIsDeleting(false);
            }
        }
    }, [versionToDelete, onRefresh]);

    const handleDeleteVersionCancel = useCallback(() => {
        setIsDeleteModalOpen(false);
        setVersionToDelete(null);
        setDeleteError(null);
    }, []);

    const handleVersionModalClose = useCallback(() => {
        setIsVersionModalOpen(false);
        setVersionToEdit(null);
    }, []);

    const handleVersionEditSuccess = useCallback(() => {
        onRefresh?.();
    }, [onRefresh]);

    // Handlers for adding a new version
    const handleAddVersion = useCallback(() => {
        setIsAddVersionModalOpen(true);
    }, []);

    const handleAddVersionClose = useCallback(() => {
        setIsAddVersionModalOpen(false);
    }, []);

    const handleAddVersionSuccess = useCallback(() => {
        setIsAddVersionModalOpen(false);
        onRefresh?.();
    }, [onRefresh]);

    if (isLoading) {
        return (
            <div className="playbook-detail-loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="playbook-error-state">
                <p>{error}</p>
                {onRetry && (
                    <button onClick={onRetry} className="retry-button">
                        Réessayer
                    </button>
                )}
            </div>
        );
    }

    if (!clause) {
        return (
            <div className="playbook-detail-empty">
                <p>Sélectionnez une clause pour voir les détails</p>
            </div>
        );
    }

    // Helper to render contract types (cliquables pour naviguer vers le contrat)
    const renderContractTypes = () => {
        if (!clause.contract_types || clause.contract_types.length === 0) return null;
        return (
            <div className="playbook-tags-list">
                {clause.contract_types.map(ct => (
                    <span
                        key={ct.id}
                        className="playbook-tag playbook-tag-clickable"
                        onClick={() => handleContractClick(ct.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleContractClick(ct.id);
                            }
                        }}
                        title={`Voir le contrat "${ct.name}"`}
                    >
                        {ct.name}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="playbook-detail">
            <div className="playbook-detail-header">
                <div className="playbook-detail-title-row">
                    <h1>{clause.name}</h1>
                    <div className="playbook-detail-actions">
                        {onEdit && (
                            <button
                                type="button"
                                className="playbook-detail-edit-btn"
                                onClick={() => onEdit(clause.id, clause.name)}
                                title="Modifier cette clause"
                                aria-label="Modifier cette clause"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                </svg>
                                <span>Modifier</span>
                            </button>
                        )}
                        {onDelete && (
                            <button
                                type="button"
                                className="playbook-detail-delete-btn"
                                onClick={() => onDelete(clause.id, clause.name)}
                                title="Supprimer cette clause"
                                aria-label="Supprimer cette clause"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18"></path>
                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                </svg>
                                <span>Supprimer</span>
                            </button>
                        )}
                    </div>
                </div>
                {renderContractTypes()}
            </div>

            {/* Terms Grid: Acceptable & Unacceptable */}
            <div className="playbook-terms-grid">
                <div className="playbook-card">
                    <div className="playbook-card-header">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon-check">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <h3>Termes préférables</h3>
                    </div>
                    <div className="playbook-card-content">
                        {clause.acceptable_terms ? (
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(clause.acceptable_terms) }} />
                        ) : (
                            <p>Aucun terme préférable défini</p>
                        )}
                    </div>
                </div>

                <div className="playbook-card">
                    <div className="playbook-card-header">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon-x">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                        <h3>Termes inacceptables</h3>
                    </div>
                    <div className="playbook-card-content">
                        {clause.unacceptable_terms ? (
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(clause.unacceptable_terms) }} />
                        ) : (
                            <p>Aucun terme inacceptable défini</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Versions Section - Style similaire aux templates */}
            <div className="playbook-accordion-section">
                <div className="versions-section-header">
                    <h2>Versions & Variantes</h2>
                    {isAdmin && (
                        <button
                            className="add-version-btn"
                            onClick={handleAddVersion}
                            title="Ajouter une version"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Ajouter
                        </button>
                    )}
                </div>
                {clause.versions.length > 0 ? (
                    <div className="playbook-accordion-list">
                        {clause.versions.map(version => {
                            const isExpanded = expandedVersionIds.has(version.id);
                            return (
                                <div key={version.id} className="template-card">
                                    {/* Card Header */}
                                    <div
                                        className="template-header"
                                        onClick={() => toggleVersion(version.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleVersion(version.id);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={isExpanded}
                                        aria-controls={`version-body-${version.id}`}
                                    >
                                        <div className="template-title-container">
                                            <h3 className="template-title">{version.name}</h3>
                                        </div>

                                        <div className="template-actions">
                                            {isAdmin && (
                                                <>
                                                    <button
                                                        className="version-action-btn version-edit-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleEditVersion(version); }}
                                                        title="Modifier cette version"
                                                        aria-label="Modifier cette version"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                                        </svg>
                                                    </button>
                                                    <button
                                                        className="version-action-btn version-delete-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteVersionRequest(version); }}
                                                        title="Supprimer cette version"
                                                        aria-label="Supprimer cette version"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M3 6h18"></path>
                                                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                                        </svg>
                                                    </button>
                                                </>
                                            )}

                                            <div className="expand-toggle">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                                                >
                                                    <polyline points="6 9 12 15 18 9"></polyline>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Body */}
                                    {isExpanded && (
                                        <div className="template-body" id={`version-body-${version.id}`}>
                                            {/* Contenu de la clause */}
                                            {version.content && (
                                                <div className="version-content-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(convertBlobPatternsToHtml(version.content)) }} />
                                            )}

                                            {/* Critères de sélection */}
                                            {version.selection_criteria && (
                                                <div className="version-selection-criteria">
                                                    <span className="version-criteria-label">Critères de sélection :</span>
                                                    <div className="version-criteria-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(version.selection_criteria) }} />
                                                </div>
                                            )}

                                            {/* Date de création */}
                                            {version.created_at && (
                                                <div className="template-date">
                                                    Créé le {new Date(version.created_at).toLocaleDateString('fr-FR', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="playbook-empty-versions">
                        <p>Aucune version définie</p>
                    </div>
                )}
            </div>

            {/* Version Edit Modal */}
            {versionToEdit && (
                <ClauseVersionModal
                    isOpen={isVersionModalOpen}
                    onClose={handleVersionModalClose}
                    mode="edit"
                    versionId={versionToEdit.id}
                    clauseId={clause.id}
                    initialData={{
                        name: versionToEdit.name,
                        content: versionToEdit.content,
                        selection_criteria: versionToEdit.selection_criteria || '',
                    }}
                    onSubmitSuccess={handleVersionEditSuccess}
                />
            )}

            {/* Version Add Modal */}
            <ClauseVersionModal
                isOpen={isAddVersionModalOpen}
                onClose={handleAddVersionClose}
                mode="add"
                clauseId={clause.id}
                onSubmitSuccess={handleAddVersionSuccess}
            />

            {/* Version Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteVersionConfirm}
                onCancel={handleDeleteVersionCancel}
                title="Supprimer cette version ?"
                message={`Êtes-vous sûr de vouloir supprimer la version "${versionToDelete?.name}" ? Cette action est irréversible.`}
                isLoading={isDeleting}
                error={deleteError}
            />
        </div>
    );
};
